/**
 * SidebarContextMenu — right-click context menu for sidebar items.
 *
 * Positioned absolutely at pointer location (same pattern as TaskContextMenu).
 * Different actions for Projects/Lists vs Areas.
 *
 * Follows master plan Fas 27b — sidebar context menu spec.
 */

import { memo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { cn } from "@/shared/utils/general/cn"
import { ObjectType, ObjectStatus } from "@/db/schema"
import { ROUTES } from "@/config/routes"
import {
  deleteProjectWithChildren,
  duplicateProject,
  convertProjectType,
} from "@/features/project-header/projectOps"
import { setContainerStatuses } from "@/features/project-header/projectStatusRequest"
import { addSection } from "@/db/operations"
import { moveToTrash } from "@/db/operations"
import { useEditFocusStore } from "@/features/task-detail/stores/editFocusStore"
import { useViewContext } from "@/app/stores/viewContext"
import type { MenuItem } from "@/features/static-data/useStaticData"

// Icons
import CheckmarkIcon from "@/shared/icons/totodoicons/stroke/checkmark-1.svg?react"
import SectionIcon from "@/shared/icons/totodoicons/stroke/section.svg?react"
import DuplicateIcon from "@/shared/icons/totodoicons/stroke/duplicate.svg?react"
import DeleteIcon from "@/shared/icons/totodoicons/stroke/delete.svg?react"
import ConvertIcon from "@/shared/icons/totodoicons/stroke/convert.svg?react"
import EditIcon from "@/shared/icons/totodoicons/stroke/edit.svg?react"
import CalendarIcon from "@/shared/icons/totodoicons/stroke/calendar.svg?react"
import TagIcon from "@/shared/icons/totodoicons/stroke/tag.svg?react"
import DeadlineIcon from "@/shared/icons/totodoicons/stroke/flag.svg?react"
import ShareIcon from "@/shared/icons/totodoicons/stroke/share.svg?react"
import Ban from "@/shared/icons/pikaicons/stroke/security/ban-left.svg?react"
import IconPickerIcon from "@/shared/icons/pikaicons/stroke/general/color-palette.svg?react"
import { ContainerIcon } from "@/shared/icons/resolveContainerIcon"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SidebarContextMenuProps {
  item: MenuItem
  /** IDs of all selected sidebar items (for bulk operations) */
  selectedIds?: string[]
  position: { x: number; y: number }
  onClose: () => void
}

// ---------------------------------------------------------------------------
// Menu item helpers (same pattern as TaskContextMenu)
// ---------------------------------------------------------------------------

interface MenuItemDef {
  label: string
  destructive?: boolean
  disabled?: boolean
  icon?: React.ReactNode
  onClick: () => void
}

function MenuAction({
  label,
  destructive,
  disabled,
  icon,
  onClick,
}: MenuItemDef) {
  return (
    <button
      className={cn(
        "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm outline-none transition-colors duration-150",
        "hover:bg-primary/10 focus:bg-primary/10",
        destructive && "text-destructive hover:bg-destructive/10",
        disabled && "pointer-events-none opacity-50",
      )}
      onClick={onClick}
      disabled={disabled}
    >
      {icon && <span className="[&_svg]:size-4">{icon}</span>}
      <span className="flex-1 text-left">{label}</span>
    </button>
  )
}

function MenuSeparator() {
  return <div className="my-1 h-px bg-foreground/5" />
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SidebarContextMenu = memo(function SidebarContextMenu({
  item,
  selectedIds = [],
  position,
  onClose,
}: SidebarContextMenuProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isBulk = selectedIds.length > 1
  const bulkIds = isBulk ? selectedIds : [item.id]

  const isProject = item.type === ObjectType.Project
  const isList = item.type === ObjectType.List
  const isArea = item.type === ObjectType.Area
  const isContainer = isProject || isList
  const isCompleted = item.status === ObjectStatus.Completed
  const typeLabel = isProject ? "project" : isList ? "list" : "area"

  // ── Handlers ──

  const handleComplete = useCallback(() => {
    onClose()
    // The clicked item decides toggle-vs-set for the whole selection; each id is then handled by its
    // own type: projects and lists are completed (asking what to do with open to-dos), areas and
    // anything else in a mixed selection are left alone.
    const newStatus = isCompleted ? ObjectStatus.Unfinished : ObjectStatus.Completed
    void setContainerStatuses(bulkIds, newStatus)
    useViewContext.getState().clearSidebarSelection()
  }, [onClose, bulkIds, isCompleted])

  const handleAddSection = useCallback(() => {
    onClose()
    if (!item.id) return
    const parentPath = `root/${item.id}`
    void addSection(t("New Heading"), parentPath)
    navigate(isProject ? ROUTES.PROJECT(item.id) : ROUTES.LIST(item.id))
  }, [onClose, item.id, isProject, navigate, t])

  const handleDuplicate = useCallback(async () => {
    onClose()
    if (!item.id) return
    const newId = await duplicateProject(item.id)
    if (newId) {
      navigate(isProject ? ROUTES.PROJECT(newId) : ROUTES.LIST(newId))
    }
  }, [onClose, item.id, isProject, navigate])

  const handleConvertType = useCallback(() => {
    onClose()
    if (!item.id) return
    void convertProjectType(item.id)
  }, [onClose, item.id])

  const handleDelete = useCallback(() => {
    onClose()
    for (const id of bulkIds) {
      if (isArea) {
        void moveToTrash(id)
      } else {
        void deleteProjectWithChildren(id)
      }
    }
    useViewContext.getState().clearSidebarSelection()
  }, [onClose, bulkIds, isArea])

  const handleRename = useCallback(() => {
    onClose()
    if (!item.id) return
    // Navigate to the container page where inline rename is available
    if (isProject) navigate(ROUTES.PROJECT(item.id))
    else if (isList) navigate(ROUTES.LIST(item.id))
    else if (isArea) navigate(ROUTES.AREA(item.id))
    // Request focus on the title field
    useEditFocusStore.getState().requestEditFocus(item.id)
  }, [onClose, item.id, isProject, isList, isArea, navigate])

  const handleCancel = useCallback(() => {
    onClose()
    const newStatus =
      item.status === ObjectStatus.Canceled ? ObjectStatus.Unfinished : ObjectStatus.Canceled
    void setContainerStatuses(bulkIds, newStatus)
    useViewContext.getState().clearSidebarSelection()
  }, [onClose, bulkIds, item.status])

  const handleSchedule = useCallback(() => {
    onClose()
    useViewContext.getState().openSchedulePicker(bulkIds)
  }, [onClose, bulkIds])

  const handleTags = useCallback(() => {
    onClose()
    useViewContext.getState().openTagPicker(bulkIds)
  }, [onClose, bulkIds])

  const handleDeadline = useCallback(() => {
    onClose()
    useViewContext.getState().openDeadlinePicker(bulkIds)
  }, [onClose, bulkIds])

  // Share opens the share dialog — for now, navigate to the container page
  // and trigger it there. Full ContainerShareDialog integration comes in C3.
  const handleShare = useCallback(() => {
    onClose()
    if (!item.id) return
    // Navigate to the container's page
    if (isProject) navigate(ROUTES.PROJECT(item.id))
    else if (isList) navigate(ROUTES.LIST(item.id))
    // TODO C3: open ContainerShareDialog directly via viewContext store
  }, [onClose, item.id, isProject, isList, navigate])

  const handleSetIcon = useCallback(() => {
    onClose()
    if (!item.id) return
    useViewContext.getState().openIconPicker(item.id)
  }, [onClose, item.id])

  // ── Viewport clamping ──
  // Ensure menu doesn't overflow the viewport
  const menuWidth = 220
  const menuHeight = isContainer ? 360 : 120
  const clampedX = Math.min(position.x, window.innerWidth - menuWidth - 16)
  const clampedY = Math.min(position.y, window.innerHeight - menuHeight - 16)

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50" onClick={onClose} />

      {/* Menu panel */}
      <div
        className="fixed z-50 min-w-[200px] overflow-hidden rounded-xl border border-foreground/5 bg-popover p-1.5 text-popover-foreground shadow-[0px_12px_32px_0px_#1111110F,0px_8px_4px_-8px_#1111110D]"
        style={{ left: clampedX, top: clampedY }}
        role="menu"
      >
        {/* Bulk header */}
        {isBulk && (
          <>
            <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground">
              {t("{{count}} items selected", { count: selectedIds.length })}
            </div>
            <MenuSeparator />
          </>
        )}

        {/* Bulk mode: simplified shared actions */}
        {isBulk ? (
          <>
            <MenuAction
              label={t("Complete All")}
              icon={<CheckmarkIcon />}
              onClick={handleComplete}
            />
            <MenuAction
              label={t("Cancel All")}
              icon={<Ban className="size-4" />}
              onClick={handleCancel}
            />
            <MenuSeparator />
            <MenuAction
              label={t("When…")}
              icon={<CalendarIcon />}
              onClick={handleSchedule}
            />
            <MenuAction
              label={t("Tags…")}
              icon={<TagIcon />}
              onClick={handleTags}
            />
            <MenuAction
              label={t("Deadline…")}
              icon={<DeadlineIcon />}
              onClick={handleDeadline}
            />
            <MenuSeparator />
            <MenuAction
              label={t("Delete All")}
              icon={<DeleteIcon />}
              destructive
              onClick={handleDelete}
            />
          </>
        ) : isContainer && (
          <>
            {/* Rename — navigate + focus title */}
            <MenuAction
              label={t("Rename")}
              icon={<EditIcon />}
              onClick={handleRename}
            />

            {/* Set custom icon */}
            <MenuAction
              label={t("Set Icon…")}
              icon={item.icon ? <ContainerIcon icon={item.icon} className="size-5" /> : <IconPickerIcon />}
              onClick={handleSetIcon}
            />

            <MenuSeparator />

            {/* Complete / Reopen */}
            <MenuAction
              label={
                isCompleted
                  ? t("Reopen")
                  : t("Complete {{type}}", { type: t(typeLabel) })
              }
              icon={<CheckmarkIcon />}
              onClick={handleComplete}
            />

            {/* Cancel / Reopen (if canceled) */}
            <MenuAction
              label={
                item.status === ObjectStatus.Canceled
                  ? t("Reopen")
                  : t("Cancel {{type}}", { type: t(typeLabel) })
              }
              icon={<Ban className="size-4" />}
              onClick={handleCancel}
            />

            <MenuSeparator />

            {/* Schedule (When) */}
            <MenuAction
              label={t("When…")}
              icon={<CalendarIcon />}
              onClick={handleSchedule}
            />

            {/* Tags */}
            <MenuAction
              label={t("Tags…")}
              icon={<TagIcon />}
              onClick={handleTags}
            />

            {/* Deadline */}
            <MenuAction
              label={t("Deadline…")}
              icon={<DeadlineIcon />}
              onClick={handleDeadline}
            />

            {/* Add Section */}
            <MenuAction
              label={t("Add Section")}
              icon={<SectionIcon />}
              onClick={handleAddSection}
            />

            <MenuSeparator />

            {/* Duplicate */}
            <MenuAction
              label={t("Duplicate")}
              icon={<DuplicateIcon />}
              onClick={() => void handleDuplicate()}
            />

            {/* Convert to list / project */}
            <MenuAction
              label={t("Convert to {{type}}", {
                type: isProject ? t("list") : t("project"),
              })}
              icon={<ConvertIcon />}
              onClick={handleConvertType}
            />

            <MenuSeparator />

            {/* Delete */}
            <MenuAction
              label={t("Delete {{type}}", { type: t(typeLabel) })}
              icon={<DeleteIcon />}
              destructive
              onClick={handleDelete}
            />

            <MenuSeparator />

            {/* Share */}
            <MenuAction
              label={t("Share…")}
              icon={<ShareIcon />}
              onClick={handleShare}
            />
          </>
        )}

        {!isBulk && isArea && (
          <>
            {/* Rename — navigates to area page */}
            <MenuAction label={t("Rename")} onClick={handleRename} />

            <MenuSeparator />

            {/* Delete area */}
            <MenuAction
              label={t("Delete area")}
              icon={<DeleteIcon />}
              destructive
              onClick={handleDelete}
            />
          </>
        )}
      </div>
    </>
  )
})
